import React, { useEffect, useState } from 'react'
import { Col, Container, Form, Row, Spinner, Stack, Tab, Toast, ToastContainer } from 'react-bootstrap'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useGetUsersQuery } from '../../../store/user/user'
import { useGenerateRecoveryCodeForUserMutation, useGetConfigQuery, useLazyListMFAStatusesQuery, useUpdateConfigMutation } from '../../../store/mfa/mfa'
import { TableColumn } from 'react-data-table-component'
import { Button } from '../../../components/Button/Button'
import { ZocksDataTable } from '../../../components/DataTable/ZocksDataTable'
import { Modal } from '../../../components/Modal/Modal'
import { Tabs } from '../../../components/Tabs/Tabs'
import { useNavigate } from 'react-router-dom'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import PrizeBadge from '../../../components/ScopedBlock/PrizeBadge.svg?url'
import { useZocksQuota } from '../../../components/ScopedBlock/hooks'
import { User } from '../../../models/User'
import { MFAStatus } from '../../../models/MFA'
import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'

const STATUS_PAGE_SIZE = 20

interface UserMFAStatus {
  userId: string
  name: string
  status: string
}

export interface GeneratedRecoveryCodeModalProps {
  recoveryCode: string
  loading: boolean
  show: boolean
  onHide: () => void
}

const GeneratedRecoveryCodeModal: React.FC<GeneratedRecoveryCodeModalProps> = ({ recoveryCode, loading, show, onHide }) => {
  const [showToast, setShowToast] = React.useState(false)

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(recoveryCode)
    setShowToast(true)
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      header={<></>}
      body={
        <Container>
          <ToastContainer position='bottom-center' className='p-3 summary-toast'>
            <Toast onClose={() => setShowToast(false)} show={showToast} delay={1500} autohide>
              <Toast.Body>Copied to clipboard!</Toast.Body>
            </Toast>
          </ToastContainer>
          <Row className='pb-3'>
            <Col>
              <h4 className='d-flex justify-content-center align-items-center'>Generated recovery code</h4>
            </Col>
          </Row>
          <Row className='pt-2'>
            <Col>
              <div className='paragraph-medium-extended'>
                Please send the recovery code below to the user asking for help. This is a one-time code that will enable them to re-gain access to their
                account.
              </div>
            </Col>
          </Row>
          <Row className='pt-4 pb-5'>
            <Col>
              <div className='label-small pb-1'>Recovery code</div>
              <div>
                <div className='rounded p-2 zocks-bg-color-gray-200'>
                  <div className='p-1 d-flex justify-content-center align-items-center'>
                    {loading ? (
                      <Spinner size='sm' />
                    ) : (
                      <div className='d-flex flex-row gap-2'>
                        <div>{recoveryCode}</div>
                        <div className='cursor-pointer' onClick={copyToClipboard}>
                          <i className='bi bi-copy' />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Col>
          </Row>
          <Row className='zocks-pb-2'>
            <Col className='d-flex align-items-center justify-content-center'>
              <Button className='button-tall w-100' variant='primary' onClick={onHide}>
                Close
              </Button>
            </Col>
          </Row>
        </Container>
      }
      showFooterActions={false}
      centered={true}
    />
  )
}

export const MFASettings: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState('consent')
  const changeTab = (key: string | null) => {
    if (key) {
      setSelectedTab(key)
    }
  }

  const TabTitle = ({ title }: { title: string }) => <div className='d-flex align-items-center'>{title}</div>
  return (
    <Container fluid className='p-0'>
      <Row className='pb-4'>
        <Col>
          <h5>Set two-factor authentication for all users on this account</h5>
        </Col>
      </Row>
      <Row className='pt-2 pb-5'>
        <Col>
          <div className='paragraph-medium'>Add an extra layer of security to your account in case your password is compromised.</div>
        </Col>
      </Row>
      <Row>
        <Col>
          <Tabs onSelect={changeTab} defaultActiveKey={selectedTab} id='mfa-settings-tabs'>
            <Tab className='mt-2' eventKey='consent' title={<TabTitle title='Enforcing policy' />}>
              <MFAEnforcingPolicy />
            </Tab>

            <Tab className='mt-2' eventKey='collaboration' title={<TabTitle title='Status' />}>
              <MFASettingsStatus />
            </Tab>
          </Tabs>
        </Col>
      </Row>
    </Container>
  )
}

const MFAEnforcingPolicy: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)
  const navigate = useNavigate()
  const { hasQuota } = useZocksQuota()
  const mfaEnforcementAvailable = hasQuota('auth.mfaEnforcement.value')
  const [mfaEnforced, setMfaEnforced] = useState<boolean>(false)
  const [mfaEnterpriseEnforced, setMfaEnterpriseEnforced] = useState<boolean>(false)
  const { data: config, isFetching } = useGetConfigQuery(accountId)
  const [updateConfig, { isLoading }] = useUpdateConfigMutation()

  useEffect(() => {
    if (config) {
      setMfaEnforced(config.mfaEnforced)
      setMfaEnterpriseEnforced(config.enterpriseEnforced)
    }
  }, [config])

  const mfaEnforcedChanged = async (value: boolean) => {
    await updateConfig({
      accountId,
      config: {
        mfaEnforced: value,
      },
    })
  }

  return (
    <Container className='d-flex flex-column align-items-start zocks-gap-2'>
      <Row></Row>
      <Row className='p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
        <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
          <Stack direction='horizontal' gap={3}>
            <div className='d-flex align-self-center gap-3 align-items-center'>
              <i className='bi bi-info-square zocks-text-primary' />
              <span className='zocks-text-gray-900'>
                Add an extra layer of security to your entire account by a Text message code or Authenticator App based Two-factor authentication setup.
              </span>
            </div>
            {!mfaEnforcementAvailable && (
              <ScopedBlock
                scope={{
                  scope: 'mfa:admin',
                  superScope: ['mfa:admin'],
                }}
                missingScopeAction={MissingScopeAction.DISABLE}
              >
                <Button
                  className='d-flex p-1 p-2 align-items-center text-nowrap'
                  variant='primary'
                  onClick={() => {
                    navigate('/account/plan')
                  }}
                >
                  <img alt='Upgrade' src={PrizeBadge} />
                  Get feature
                </Button>
              </ScopedBlock>
            )}
          </Stack>
        </Col>
      </Row>
      {mfaEnterpriseEnforced ? (
        <Row className='p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
          <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
            <div className='d-flex align-self-center gap-3 align-items-center'>
              <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
              <span className='zocks-text-gray-900'>Two-factor authentication enforcement is managed at the enterprise level.</span>
            </div>
          </Col>
        </Row>
      ) : null}
      <Row className='gap-3 p-0 align-items-center'>
        <Col xs='auto' className='p-0'>
          <div className='paragraph-medium'>Enforce two-factor authentication for all users on this account</div>
        </Col>
        <Col className='p-0'>
          <ScopedBlock
            scope={{
              scope: 'account:update',
              superScope: ['account:admin'],
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
          >
            <Form.Switch
              disabled={!mfaEnforcementAvailable || isFetching || isLoading || mfaEnterpriseEnforced}
              checked={mfaEnforced}
              onChange={(e) => mfaEnforcedChanged(e.currentTarget.checked)}
            />
          </ScopedBlock>
        </Col>
      </Row>
    </Container>
  )
}

const MFASettingsStatus: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)
  const [perPage, setPerPage] = useState(STATUS_PAGE_SIZE)
  const [page, setPage] = useState(1)
  const [userMFAStatuses, setUserMFAStatuses] = useState<UserMFAStatus[]>([])
  const [showRecoveryCodeModal, setShowRecoveryCodeModal] = useState(false)
  const [recoveryCode, setRecoveryCode] = useState<string>('')
  const [users, setUsers] = useState<User[]>([])
  const [statuses, setStatuses] = useState<MFAStatus[]>([])
  const [totalElements, setTotalElements] = useState<number>(0)
  const { data, isFetching } = useGetUsersQuery(
    { accountId: accountId, pageRequest: { page: page - 1, size: perPage }, identityTypes: ['idp'] },
    { refetchOnMountOrArgChange: true },
  )
  const [listMFAStatuses] = useLazyListMFAStatusesQuery()
  const [triggerGenerateRecoveryCode, { isLoading: isGenerateRecoveryCodeLoading }] = useGenerateRecoveryCodeForUserMutation()

  const handlePageChange = (page: number) => {
    setPage(page)
  }

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPage(page)
    setPerPage(newPerPage)
  }

  useEffect(() => {
    setUsers(data?.data || [])
  }, [data])

  useEffect(() => {
    setTotalElements(data?.page?.totalElements || 0)
  }, [data])

  useEffect(() => {
    if (!users.length) {
      return
    }
    const userIds = users.map((user) => user.id)
    listMFAStatuses({ accountId, request: { userIds } }).then(({ data: mfaStatuses }) => {
      if (mfaStatuses) {
        setStatuses(mfaStatuses)
      }
    })
  }, [users])

  useEffect(() => {
    setUserMFAStatuses(
      users.map((user) => ({
        userId: user.id,
        name: getUserDisplayName(user),
        status: statuses.find((s) => s.userId === user.id)?.status || 'Loading…',
      })),
    )
  }, [users, statuses])

  const generateRecoveryCode = async (userId: string) => {
    setShowRecoveryCodeModal(true)
    const { data } = await triggerGenerateRecoveryCode({ accountId, userId })
    if (data) {
      setRecoveryCode(data)
    }
  }

  const columns: TableColumn<UserMFAStatus>[] = [
    {
      name: `${totalElements} team member${totalElements > 1 ? 's' : ''}`,
      selector: (row) => row.name,
      sortable: true,
      width: 'auto',
    },
    {
      name: 'Status',
      selector: (row) => row.status,
      sortable: true,
      width: '7.25rem',
      cell: (row) => (
        <div className={`${row.status === 'protected' ? 'text-success' : 'zocks-text-gray-500'} paragraph-small text-capitalize`}>{row.status}</div>
      ),
    },
    {
      selector: (row) => row.status,
      sortable: false,
      button: true,
      width: '9rem',
      cell: (row) =>
        row.status === 'protected' && (
          <Button className='button-medium' variant='primaryGhost' onClick={() => generateRecoveryCode(row.userId)}>
            Recovery code
          </Button>
        ),
    },
  ]

  return (
    <>
      <GeneratedRecoveryCodeModal
        recoveryCode={recoveryCode}
        loading={isGenerateRecoveryCodeLoading}
        show={showRecoveryCodeModal}
        onHide={() => setShowRecoveryCodeModal(false)}
      />
      <Container className='p-0'>
        <Row className='pt-4'>
          <Col className='p-0'>
            <ZocksDataTable
              columns={columns}
              data={userMFAStatuses}
              progressPending={isFetching}
              pagination
              paginationServer
              paginationTotalRows={data?.page.totalElements}
              onChangeRowsPerPage={handlePerRowsChange}
              onChangePage={handlePageChange}
              paginationPerPage={STATUS_PAGE_SIZE}
            />
          </Col>
        </Row>
      </Container>
    </>
  )
}
